import { useState } from "react";
import { completePasswordReset } from "@/lib/auth";
import { GraduationCap, Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const userId = params.get("userId") ?? "";
  const secret = params.get("secret") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(
    !userId || !secret ? "Invalid or expired reset link. Please request a new one." : null
  );

  const mismatch = confirm.length > 0 && password !== confirm;
  const weak = password.length > 0 && password.length < 8;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(null);
    setLoading(true);
    try {
      await completePasswordReset(userId, secret, password);
      setDone(true);
    } catch (err: any) {
      setError(err.message ?? "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border shadow-sm p-8 w-full max-w-sm space-y-6">

        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Choose a new password</h1>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 py-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <p className="text-sm text-gray-700 font-medium text-center">Password updated!</p>
              <p className="text-sm text-gray-500 text-center">You can now sign in with your new password.</p>
            </div>
            <a
              href="/login"
              className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
            >
              Go to sign in
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">

            {error && (
              <div className="flex items-start gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="text-sm text-gray-600 mb-1 block">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoFocus
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={`w-full border rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${weak ? "border-amber-400" : ""}`}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {weak && <p className="text-xs text-amber-600 mt-1">Must be at least 8 characters</p>}
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat your password"
                  className={`w-full border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${mismatch ? "border-red-400" : ""}`}
                />
              </div>
              {mismatch && <p className="text-xs text-red-500 mt-1">Passwords don't match</p>}
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirm || mismatch || weak || !userId || !secret}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Updating…" : "Set new password"}
            </button>

            <a href="/forgot-password"
              className="block text-center text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Request a new link
            </a>
          </form>
        )}
      </div>
    </div>
  );
}
